import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, ChevronLeft, Download, Printer, Calendar, TrendingUp, Users, Building2, CreditCard, RefreshCw, PieChart, Activity } from "lucide-react";
import axios from "axios";
import { SiteSelector, CommercialProvider, useSite } from "./SiteSelector";

const API = '/api';

const ReportsContent = () => {
  const navigate = useNavigate();
  const { selectedSite } = useSite();
  
  const [summary, setSummary] = useState(null);
  const [previousSummary, setPreviousSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => { fetchData(); }, [selectedSite, dateFrom, dateTo]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = `${API}/commercial/summary`;
      const params = [];
      if (selectedSite !== "all") params.push(`site_id=${selectedSite}`);
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (params.length > 0) url += `?${params.join("&")}`;
      
      const res = await axios.get(url);
      setSummary(res.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const exportReport = () => {
    if (!summary) return;
    const rows = [
      ["Commercial Management Report"],
      ["Generated", new Date().toLocaleString()],
      ["Site Filter", selectedSite === "all" ? "All Sites" : selectedSite],
      [],
      ["Revenue Summary"],
      ["Today", summary.revenue?.today?.display],
      ["Yesterday", summary.revenue?.yesterday?.display],
      ["This Week", summary.revenue?.week?.display],
      ["This Month", summary.revenue?.month?.display],
      ["This Year", summary.revenue?.year?.display],
      ["All Time", summary.revenue?.all_time?.display],
      [],
      ["Payments"],
      ["Total", summary.payments?.total],
      ["Completed", summary.payments?.completed],
      ["Pending", summary.payments?.pending],
      ["Failed", summary.payments?.failed],
      [],
      ["Resident Passes"],
      ["Total", summary.resident_passes?.total],
      ["Active", summary.resident_passes?.active],
      ["Pending", summary.resident_passes?.pending],
      ["Expired", summary.resident_passes?.expired],
      [],
      ["Staff Passes"],
      ["Total", summary.staff_passes?.total],
      ["Active", summary.staff_passes?.active],
      ["Pending", summary.staff_passes?.pending],
      ["Expired", summary.staff_passes?.expired],
      [],
      ["Retailers"],
      ["Total", summary.retailers?.total],
      ["Active", summary.retailers?.active],
      [],
      ["Interest Pipeline"],
      ["Total", summary.interests?.total],
      ["New", summary.interests?.new],
      ["Contacted", summary.interests?.contacted],
      ["Converted", summary.interests?.converted],
      ["Conversion Rate", summary.interests?.conversion_rate],
    ];
    const csv = rows.map(row => row.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `commercial-report-${new Date().toISOString().split('T')[0]}.csv`; a.click();
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="commercial-reports">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/admin/commercial")} className="p-2 hover:bg-gray-100 rounded-lg"><ChevronLeft className="w-5 h-5" /></button>
            <div><h1 className="text-xl font-bold text-gray-900">Reports</h1><p className="text-sm text-gray-500">Commercial analytics & insights</p></div>
          </div>
          <div className="flex items-center gap-2">
            <SiteSelector />
            <button onClick={exportReport} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Download className="w-4 h-4" /> Export CSV</button>
            <button onClick={() => window.print()} className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"><Printer className="w-4 h-4" /> Print</button>
          </div>
        </div>
      </div>

      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-500">Date Range:</span>
          </div>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <span className="text-gray-400">to</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
          <button onClick={fetchData} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700">Apply</button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {loading ? (
          <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-gray-400 animate-spin" /></div>
        ) : summary ? (
          <>
            {/* Revenue Summary */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <h3 className="text-sm font-semibold text-gray-700 mb-4">Revenue Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {[
                  { label: "Today", value: summary.revenue?.today?.display, path: summary.revenue?.today?.drill_down },
                  { label: "Yesterday", value: summary.revenue?.yesterday?.display, path: summary.revenue?.yesterday?.drill_down },
                  { label: "This Week", value: summary.revenue?.week?.display, path: summary.revenue?.week?.drill_down },
                  { label: "This Month", value: summary.revenue?.month?.display, path: summary.revenue?.month?.drill_down },
                  { label: "This Year", value: summary.revenue?.year?.display, path: summary.revenue?.year?.drill_down },
                  { label: "All Time", value: summary.revenue?.all_time?.display, path: summary.revenue?.all_time?.drill_down },
                ].map((item) => (
                  <div key={item.label} onClick={() => navigate(item.path)} className="p-4 bg-gray-50 rounded-lg cursor-pointer hover:bg-blue-50 transition-colors">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className="text-lg font-bold text-gray-900">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Activity Summary */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4"><CreditCard className="w-5 h-5 text-blue-600" /><h3 className="text-sm font-semibold text-gray-700">Payments</h3></div>
                <div className="space-y-2">
                  <div onClick={() => navigate(summary.payments?.drill_down?.all)} className="flex justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"><span className="text-sm text-gray-600">Total</span><span className="font-bold">{summary.payments?.total}</span></div>
                  <div onClick={() => navigate(summary.payments?.drill_down?.completed)} className="flex justify-between p-2 hover:bg-green-50 rounded cursor-pointer"><span className="text-sm text-green-600">Completed</span><span className="font-bold text-green-600">{summary.payments?.completed}</span></div>
                  <div onClick={() => navigate(summary.payments?.drill_down?.pending)} className="flex justify-between p-2 hover:bg-yellow-50 rounded cursor-pointer"><span className="text-sm text-yellow-600">Pending</span><span className="font-bold text-yellow-600">{summary.payments?.pending}</span></div>
                  <div onClick={() => navigate(summary.payments?.drill_down?.failed)} className="flex justify-between p-2 hover:bg-red-50 rounded cursor-pointer"><span className="text-sm text-red-600">Failed</span><span className="font-bold text-red-600">{summary.payments?.failed}</span></div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4"><Users className="w-5 h-5 text-purple-600" /><h3 className="text-sm font-semibold text-gray-700">Resident Passes</h3></div>
                <div className="space-y-2">
                  <div onClick={() => navigate(summary.resident_passes?.drill_down?.all)} className="flex justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"><span className="text-sm text-gray-600">Total</span><span className="font-bold">{summary.resident_passes?.total}</span></div>
                  <div onClick={() => navigate(summary.resident_passes?.drill_down?.active)} className="flex justify-between p-2 hover:bg-green-50 rounded cursor-pointer"><span className="text-sm text-green-600">Active</span><span className="font-bold text-green-600">{summary.resident_passes?.active}</span></div>
                  <div onClick={() => navigate(summary.resident_passes?.drill_down?.pending)} className="flex justify-between p-2 hover:bg-yellow-50 rounded cursor-pointer"><span className="text-sm text-yellow-600">Pending</span><span className="font-bold text-yellow-600">{summary.resident_passes?.pending}</span></div>
                  <div onClick={() => navigate(summary.resident_passes?.drill_down?.expired)} className="flex justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"><span className="text-sm text-gray-500">Expired</span><span className="font-bold text-gray-500">{summary.resident_passes?.expired}</span></div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4"><Building2 className="w-5 h-5 text-orange-600" /><h3 className="text-sm font-semibold text-gray-700">Staff Passes</h3></div>
                <div className="space-y-2">
                  <div onClick={() => navigate(summary.staff_passes?.drill_down?.all)} className="flex justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"><span className="text-sm text-gray-600">Total</span><span className="font-bold">{summary.staff_passes?.total}</span></div>
                  <div onClick={() => navigate(summary.staff_passes?.drill_down?.active)} className="flex justify-between p-2 hover:bg-green-50 rounded cursor-pointer"><span className="text-sm text-green-600">Active</span><span className="font-bold text-green-600">{summary.staff_passes?.active}</span></div>
                  <div onClick={() => navigate(summary.staff_passes?.drill_down?.pending)} className="flex justify-between p-2 hover:bg-yellow-50 rounded cursor-pointer"><span className="text-sm text-yellow-600">Pending</span><span className="font-bold text-yellow-600">{summary.staff_passes?.pending}</span></div>
                  <div onClick={() => navigate(summary.staff_passes?.drill_down?.expired)} className="flex justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"><span className="text-sm text-gray-500">Expired</span><span className="font-bold text-gray-500">{summary.staff_passes?.expired}</span></div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-4"><Activity className="w-5 h-5 text-indigo-600" /><h3 className="text-sm font-semibold text-gray-700">Interest Pipeline</h3></div>
                <div className="space-y-2">
                  <div onClick={() => navigate(summary.interests?.drill_down?.all)} className="flex justify-between p-2 hover:bg-gray-50 rounded cursor-pointer"><span className="text-sm text-gray-600">Total</span><span className="font-bold">{summary.interests?.total}</span></div>
                  <div onClick={() => navigate(summary.interests?.drill_down?.new)} className="flex justify-between p-2 hover:bg-blue-50 rounded cursor-pointer"><span className="text-sm text-blue-600">New</span><span className="font-bold text-blue-600">{summary.interests?.new}</span></div>
                  <div onClick={() => navigate(summary.interests?.drill_down?.contacted)} className="flex justify-between p-2 hover:bg-yellow-50 rounded cursor-pointer"><span className="text-sm text-yellow-600">Contacted</span><span className="font-bold text-yellow-600">{summary.interests?.contacted}</span></div>
                  <div onClick={() => navigate(summary.interests?.drill_down?.converted)} className="flex justify-between p-2 hover:bg-green-50 rounded cursor-pointer"><span className="text-sm text-green-600">Converted</span><span className="font-bold text-green-600">{summary.interests?.converted}</span></div>
                </div>
              </div>
            </div>

            {/* Conversion Rate */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-700">Pipeline Conversion Rate</h3>
                  <p className="text-3xl font-bold text-green-600 mt-2">{summary.interests?.conversion_rate || "0%"}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Converted / Total Interests</p>
                  <p className="text-lg font-medium text-gray-700">{summary.interests?.converted || 0} / {summary.interests?.total || 0}</p>
                </div>
              </div>
            </div>
          </>
        ) : (
          <p className="text-center text-gray-500 py-12">Failed to load report data</p>
        )}
      </div>
    </div>
  );
};

export default function CommercialReports() {
  return (
    <CommercialProvider>
      <ReportsContent />
    </CommercialProvider>
  );
}
